import React, { useState, useEffect } from 'react';

export default function SolapurDealsWidget() {
  const [isOpen, setIsOpen] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if dismissed for current session
    const dismissed = sessionStorage.getItem('solapurDealsDismissed') === 'true';
    setIsDismissed(dismissed);

    // Reset dismissed state on user login or logout (detected via userUpdated event)
    const handleUserUpdate = () => {
      sessionStorage.removeItem('solapurDealsDismissed');
      setIsDismissed(false);
    };

    window.addEventListener('userUpdated', handleUserUpdate);
    return () => {
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, []);

  const handleDismiss = (e) => {
    if (e) e.stopPropagation();
    sessionStorage.setItem('solapurDealsDismissed', 'true');
    setIsDismissed(true);
    setIsOpen(false);
  };

  if (isDismissed) return null;

  return (
    <div className="fixed bottom-5 left-5 z-50 font-sans">
      {isOpen ? (
        // Chat Window Card
        <div className="w-80 bg-slate-900 border border-amber-500/30 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-5 duration-300">
          {/* Card Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-4 text-white flex justify-between items-center relative">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white text-orange-600 rounded-full flex items-center justify-center font-bold text-lg shadow-inner">
                SD
              </div>
              <div className="text-left">
                <div className="font-bold flex items-center gap-1.5 leading-none">
                  Solapur Deals
                  <span className="w-2.5 h-2.5 bg-green-400 rounded-full inline-block border-2 border-orange-500 animate-pulse"></span>
                </div>
                <span className="text-[10px] text-orange-100 font-medium tracking-wide">coming soon</span>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-white/80 hover:text-white text-xl font-bold transition-colors focus:outline-none"
              title="Close and start ordering"
            >
              ×
            </button>
          </div>

          {/* Card Body */}
          <div className="p-4 space-y-3 bg-[#0d1117] text-left">
            <div className="bg-[#161b22] border border-gray-800 rounded-xl p-3 text-xs text-gray-300 relative">
              <p className="leading-relaxed mb-2 font-bold text-amber-400">
                📢 "Our City Our Deals"
              </p>
              <p className="leading-relaxed mb-2">
                If you need something locally, come to <a href="https://solapurdeals.in" target="_blank" rel="noopener noreferrer" className="text-amber-300 hover:text-amber-200 underline font-semibold">solapurdeals.in</a>. It is an application of local deals in Solapur.
              </p>
              <div className="mt-2 pt-2 border-t border-gray-800/60">
                <span className="text-gray-400 block mb-0.5 text-[10px]">For contact or support:</span>
                <a
                  href="mailto:solapurdeals@gmail.com"
                  className="text-amber-400 hover:text-amber-300 font-semibold underline text-sm break-all"
                >
                  solapurdeals@gmail.com
                </a>
              </div>
            </div>

            {/* Visit Website Button */}
            <a
              href="https://solapurdeals.in"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-center py-2.5 rounded-xl font-semibold text-sm hover:from-amber-600 hover:to-orange-600 transition-all shadow-md shadow-amber-500/10"
            >
              Visit solapurdeals.in
            </a>

            {/* Dismiss completely option */}
            <button
              onClick={handleDismiss}
              className="block w-full text-center text-[10px] text-gray-500 hover:text-gray-400 underline transition-colors pt-1"
            >
              Dismiss support widget for this session
            </button>
          </div>
        </div>
      ) : (
        // Floating Bubble Button (WhatsApp Style)
        <div className="relative group">
          {/* Main Bubble Button */}
          <button
            onClick={() => setIsOpen(true)}
            className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 focus:outline-none border-2 border-white/20"
            title="Solapur Deals Support"
          >
            {/* WhatsApp resembling icon or general message icon */}
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
              />
            </svg>
            
            {/* Pulsing online badge */}
            <span className="absolute top-0 right-0 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-slate-900 flex items-center justify-center text-[8px] font-bold text-white">
              1
            </span>
          </button>

          {/* Dismiss widget badge (x icon) */}
          <button
            onClick={handleDismiss}
            className="absolute -top-1.5 -left-1.5 bg-slate-800 hover:bg-slate-700 text-gray-400 hover:text-white w-5 h-5 rounded-full flex items-center justify-center text-xs border border-gray-700 shadow-md transition-all focus:outline-none"
            title="Dismiss widget completely"
          >
            ×
          </button>

          {/* Hover tooltip */}
          <span className="absolute left-16 top-1/2 -translate-y-1/2 bg-slate-900 text-white text-xs px-2.5 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-md border border-gray-800 pointer-events-none whitespace-nowrap">
            Solapur Deals Support
          </span>
        </div>
      )}
    </div>
  );
}
